import { useCallback, useEffect, useRef, useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import {
    resendSignUpCode,
    signInWithRedirect,
    type SignInInput,
    type SignInOutput,
    type SignUpInput,
} from "aws-amplify/auth";
import { setCookie } from "@/util/cookie.ts";
import { rememberGaSessionId } from "@/app/auth/gaSession.ts";
import { describeOauthError } from "./oauthError.ts";

// cognito-local (the local dev backend, jagregory/cognito-local) doesn't
// implement SRP, so it rejects InitAuth with USER_SRP_AUTH. Locally we use
// USER_PASSWORD_AUTH for every InitAuth (sign-in and post-sign-up auto
// sign-in); deployed environments request passkeys or email codes via USER_AUTH.
const isLocalDev = import.meta.env.MODE === "development";

/**
 * Whether sign-in asks for a password. Only locally, and for the same reason
 * sign-up does: cognito-local implements neither passkeys nor email codes, so
 * a seeded local account has nothing else to sign in with. Everywhere else the
 * app offers a passkey or an emailed code without factor discovery. Cognito
 * still requires PASSWORD in its pool policy, so this UI does not enforce a
 * server-side prohibition on passwords.
 */
export const signInNeedsPassword = isLocalDev;

/**
 * Whether passkeys are offered at all. Disabled locally because cognito-local
 * implements none of the WebAuthn api (Associate/List/DeleteWebAuthnCredential),
 * so every call fails with an "unsupported x-amz-target header" error.
 */
export const supportsPasskeys = !isLocalDev;

/** Sign-in options for this environment (password auth locally, default otherwise). */
export const localSignInOptions: SignInInput["options"] = isLocalDev
    ? { authFlowType: "USER_PASSWORD_AUTH" }
    : undefined;

/**
 * Whether sign-up asks for a password. Only locally: cognito-local sends no
 * email codes and has no USER_AUTH, so a local account signs in with a
 * password. Everywhere else an account starts without one, and the code that
 * confirms the address is also what signs it in.
 */
export const signUpNeedsPassword = isLocalDev;

/**
 * How `signUp` signs the new account in once its code is confirmed. USER_AUTH
 * hands Cognito the session ConfirmSignUp returned, which proves the code was
 * just entered, so nobody is asked for a second one. Locally it is the password
 * they typed.
 */
export const signUpAutoSignIn: NonNullable<
    SignUpInput["options"]
>["autoSignIn"] = isLocalDev
    ? { authFlowType: "USER_PASSWORD_AUTH" }
    : { authFlowType: "USER_AUTH" };

export const AUTH_ROUTES = {
    signIn: "/login",
    signUp: "/signup",
    signUpUtmScout: "/signup-utm-scout",
    confirmSignUp: "/confirm-signup",
    emailCode: "/email-code",
} as const;

/** The `name` of an Amplify/Cognito error (`UserNotFoundException`, ...). */
function errorName(err: unknown): string | undefined {
    if (err && typeof err === "object" && "name" in err) {
        const name = (err as { name?: unknown }).name;
        return typeof name === "string" ? name : undefined;
    }
    return undefined;
}

/** Map an Amplify auth error to a user-facing message. */
export function authErrorMessage(err: unknown): string {
    const name = errorName(err);
    if (name) {
        const message = (err as { message?: string }).message;
        switch (name) {
            case "NotAuthorizedException":
            case "UserNotFoundException":
                // Deliberately vague, and not "incorrect password": it answers
                // for an unknown address as well as a refused credential, and
                // which of the two it was is not the caller's to learn.
                return "We could not sign you in. Check your details and try again.";
            case "CodeMismatchException":
                return "That code is not correct. Please try again.";
            case "ExpiredCodeException":
                return "That code has expired. Please request a new one.";
            case "LimitExceededException":
                return "Too many attempts. Please wait a moment and try again.";
            case "UsernameExistsException":
                return "An account with this email already exists.";
            case "InvalidPasswordException":
                return message || "Password does not meet the requirements.";
            case "UserNotConfirmedException":
                return "Please confirm your email to continue.";
            case "InvalidParameterException":
                return message || "Please check your details and try again.";
            // A refusal from the pre sign-up trigger, in Cognito's wrapper
            case "UserLambdaValidationException":
                return (
                    describeOauthError(message) ??
                    "Something went wrong. Please try again."
                );
            // Ours, and already written for the person reading it.
            case "SignInNotPossible":
                return message || "Something went wrong. Please try again.";
            // A bare Error is ours too, but its text is for a log: Amplify
            // resolves rather than throws for a step we do not handle, so the
            // throw below would otherwise put "Unsupported sign-in step: …" on
            // the screen.
            case "Error":
                return "Something went wrong. Please try again.";
            default:
                return message || "Something went wrong. Please try again.";
        }
    }
    return "Something went wrong. Please try again.";
}

/**
 * Error copy for a passkey attempt.
 *
 * Amplify raises every WebAuthn failure as a PasskeyError whose name is a
 * PasskeyErrorCode and whose message is written for whoever wrote the code
 * ("Passkey authentication ceremony has been canceled."). The button is now
 * offered to everyone rather than only to accounts that discovery said had a
 * passkey, so these are reached routinely: a cancelled sheet, a timeout, a
 * device holding no matching credential.
 */
export function passkeyErrorMessage(err: unknown): string {
    switch (errorName(err)) {
        case "PasskeyAuthenticationCanceled":
        case "PasskeyOperationAborted":
        case "PasskeyRetrievalFailed":
            return "That passkey attempt did not finish. Try again, or ask for an email code.";
        case "PasskeyNotSupported":
        case "RelyingPartyMismatch":
        case "InvalidPasskeyAuthenticationOptions":
            return "This device cannot use a passkey here. Ask for an email code instead.";
        default:
            return authErrorMessage(err);
    }
}

/**
 * A sign-in Cognito will not complete, carrying words for the person in front
 * of it rather than the step name.
 */
export class SignInNotPossibleError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "SignInNotPossible";
    }
}

/**
 * Route a `signIn`/`confirmSignIn` result to the next screen. When the result
 * is already signed in, the Hub `signedIn` event flips the app to
 * authenticated, so there is nothing to navigate to.
 */
export async function handleSignInNextStep(
    result: SignInOutput,
    email: string,
    navigate: NavigateFunction,
): Promise<void> {
    if (result.isSignedIn) {
        return;
    }
    const step = result.nextStep.signInStep;
    switch (step) {
        // A pool not yet migrated may still offer only passwords to an
        // account with native MFA. There is no password or Totp screen now.
        case "CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION":
        case "CONFIRM_SIGN_IN_WITH_PASSWORD":
        case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
            throw new SignInNotPossibleError(
                "This account cannot use passwordless sign-in yet. Email support@spectaclehq.com and we will sort it out.",
            );
        case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
            // The code screen needs the address both to say where the code
            // went and to send another one.
            navigate(AUTH_ROUTES.emailCode, { state: { email } });
            break;
        // Both of these are password steps that no longer have a screen, and
        // Amplify *resolves* with them rather than throwing -- so without a
        // case here they would reach the default below and show its message.
        // RESET_PASSWORD survives AdminResetUserPassword, a user import and
        // threat protection; NEW_PASSWORD_REQUIRED needs a password attempt,
        // which only local dev can make.
        case "RESET_PASSWORD":
            throw new SignInNotPossibleError(
                "This account has to be reset before it can sign in. Email support@spectaclehq.com and we will sort it out.",
            );
        case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
            throw new SignInNotPossibleError(
                signInNeedsPassword
                    ? "This account still has a temporary password. Give it a permanent one: make set_local_user_pass EMAIL=…"
                    : "This account cannot sign in. Email support@spectaclehq.com and we will sort it out.",
            );
        case "CONFIRM_SIGN_UP":
            // Unconfirmed account tried to sign in — send a fresh code.
            await resendSignUpCode({ username: email });
            navigate(AUTH_ROUTES.confirmSignUp, { state: { email } });
            break;
        case "DONE":
            break;
        default:
            throw new Error(`Unsupported sign-in step: ${step}`);
    }
}

/** Start the Google OAuth redirect, mirroring the previous cookie behavior. */
export async function continueWithGoogle(isUtmScout: boolean): Promise<void> {
    setCookie("potentialSignUp", "true", 14);
    rememberGaSessionId();
    if (isUtmScout) {
        setCookie("isUtmScout", "true", 15, "minutes");
    }
    await signInWithRedirect({
        provider: "Google",
        options: { prompt: "SELECT_ACCOUNT" },
    });
}

/** 60-second resend cooldown shared by the code-entry screens. */
export function useResendCooldown() {
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const deadlineRef = useRef<number | null>(null);

    useEffect(() => {
        if (secondsRemaining <= 0) {
            return;
        }
        const interval = setInterval(() => {
            if (deadlineRef.current === null) {
                return;
            }
            const remaining = Math.max(0, deadlineRef.current - Date.now());
            setSecondsRemaining(Math.ceil(remaining / 1000));
        }, 1000);
        return () => clearInterval(interval);
    }, [secondsRemaining]);

    const startCooldown = useCallback(() => {
        deadlineRef.current = Date.now() + 60000;
        setSecondsRemaining(60);
    }, []);

    return {
        canResend: secondsRemaining <= 0,
        secondsRemaining,
        startCooldown,
    };
}
